import { useCallback, useEffect, useState } from 'react';
import type { ProviderHealth, ProviderHealthState } from '../../shared/api';
import { ApiError, NetworkError, type ApiClient } from '../api';
import { sessionApi, sessionStore, useSessionToken, type SessionStore } from '../state';

type HealthState =
  | { status: 'loading' }
  | { status: 'ok'; detail: string }
  | { status: 'unreachable'; detail: string };

type ProvidersState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; providers: ProviderHealth[]; checkedAt: string };

const HEALTH_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/healthz`;

const PROVIDER_LABELS: Record<string, string> = {
  gcs: 'Google Cloud Storage',
  github: 'GitHub',
};

const STATE_LABELS: Record<ProviderHealthState, string> = {
  ok: 'OK',
  unauthorized: 'Credential rejected',
  rate_limited: 'Rate limited',
  unreachable: 'Unreachable',
  not_configured: 'Not configured',
};

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.code}, HTTP ${err.status})`;
  if (err instanceof NetworkError) return `Network error: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

export interface DashboardPageProps {
  /** Injectable for tests; defaults to the app-wide {@link sessionStore} singleton. */
  store?: SessionStore;
  /** Injectable for tests; defaults to the token-bound {@link sessionApi} client. */
  api?: ApiClient;
}

/**
 * Landing view for the support GUI: API reachability plus live provider
 * credential status.
 *
 * The provider card is the answer to "I rotated the GitHub token and the
 * Releases page still 401s". Cloudflare Pages binds secrets per *deployment*,
 * so setting a secret does not change what the running deployment sees — this
 * card reports what the live deployment is actually using (by credential class
 * and length, never the value) and whether GitHub accepts it. See
 * support-gui/README.md for the rotation runbook.
 */
export default function DashboardPage({
  store = sessionStore,
  api = sessionApi,
}: DashboardPageProps) {
  const token = useSessionToken(store);
  const [health, setHealth] = useState<HealthState>({ status: 'loading' });
  const [providers, setProviders] = useState<ProvidersState>({ status: 'idle' });
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetch(HEALTH_URL, { signal: controller.signal })
      .then(async (res) => {
        const body = await res.text();
        setHealth(
          res.ok
            ? { status: 'ok', detail: body || `HTTP ${res.status}` }
            : { status: 'unreachable', detail: `HTTP ${res.status}: ${body}` },
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setHealth({
          status: 'unreachable',
          detail: err instanceof Error ? err.message : String(err),
        });
      });
    return () => controller.abort();
  }, []);

  // Provider probes need the operator token, so they wait for sign-in.
  useEffect(() => {
    if (token === null) {
      setProviders({ status: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    setProviders({ status: 'loading' });
    api.checkProviders(controller.signal).then(
      (response) =>
        setProviders({
          status: 'loaded',
          providers: response.providers,
          checkedAt: response.checkedAt,
        }),
      (err: unknown) => {
        if (!(err instanceof DOMException && err.name === 'AbortError'))
          setProviders({ status: 'error', message: describeError(err) });
      },
    );
    return () => controller.abort();
  }, [api, token, reloadNonce]);

  const recheck = useCallback(() => setReloadNonce((nonce) => nonce + 1), []);

  return (
    <section className="dashboard">
      <h1>Support Dashboard</h1>
      <div className={`health-card health-card--${health.status}`}>
        <h2>API Health</h2>
        <p className="health-status">
          {health.status === 'loading' && 'Checking…'}
          {health.status === 'ok' && 'Reachable'}
          {health.status === 'unreachable' && 'Unreachable'}
        </p>
        {'detail' in health && <p className="health-detail">{health.detail}</p>}
        <p className="health-endpoint">
          Endpoint: <code>{HEALTH_URL}</code>
        </p>
      </div>

      <div className="health-card provider-health-card">
        <div className="provider-health-header">
          <h2>Provider credentials</h2>
          <button type="button" onClick={recheck} disabled={providers.status === 'loading'}>
            {providers.status === 'loading' ? 'Checking…' : 'Re-check'}
          </button>
        </div>

        {providers.status === 'idle' && (
          <p className="panel-note">Sign in with an operator token to probe provider credentials.</p>
        )}
        {providers.status === 'loading' && <p className="panel-note">Probing GCS and GitHub…</p>}
        {providers.status === 'error' && (
          <p className="health-detail" role="alert">
            Probe failed: {providers.message}
          </p>
        )}

        {providers.status === 'loaded' && (
          <>
            <ul className="provider-health-list">
              {providers.providers.map((provider) => (
                <li key={provider.provider} className={`provider-health provider-health--${provider.state}`}>
                  <span className="provider-health-name">
                    {PROVIDER_LABELS[provider.provider] ?? provider.provider}
                  </span>
                  <span className={`badge badge--health-${provider.state}`}>
                    {STATE_LABELS[provider.state]}
                  </span>
                  <span className="provider-health-detail">{provider.detail}</span>
                  <span className="provider-health-credential">
                    Credential in use: <code>{provider.credential ?? 'none'}</code>
                    {provider.scopes !== undefined && provider.scopes !== null && (
                      <>
                        {' · '}
                        scopes:{' '}
                        <code>{provider.scopes === '' ? 'none (fine-grained PAT)' : provider.scopes}</code>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <p className="health-endpoint">Probed {new Date(providers.checkedAt).toLocaleString()}</p>
            {providers.providers.some(
              (p) => p.provider === 'github' && p.state !== 'ok',
            ) && (
              <p className="panel-note">
                A rejected GitHub credential degrades the Release Selector to GCS artifacts only.
                Pages binds secrets per deployment: after{' '}
                <code>wrangler pages secret put GITHUB_TOKEN</code> you must redeploy before the
                live deployment picks it up. Full runbook:{' '}
                <code>support-gui/README.md</code>.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
